/*
 * TissueMotif Background Engine
 * Version 1.2.0
 *
 * Client-side bridge between TissueMotif Creative Studio and the
 * Shopify App Proxy background-removal endpoint.
 *
 * V1.2.0 preservation:
 * - PNG and WebP files that already have meaningful transparency
 *   bypass PhotoRoom and continue directly into Creative Studio.
 * - Opaque images continue through the existing endpoint.
 * - Unusable output is rejected; callers may explicitly retain the original.
 */
(function () {
  'use strict';

  const VERSION = '1.2.0';
  const MAX_FILE_BYTES = 20 * 1024 * 1024;
  const SUPPORTED_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/webp'
  ]);

  /*
   * Transparency detection is intentionally conservative.
   *
   * We do NOT skip PhotoRoom just because one or two anti-aliased pixels
   * have alpha. A PNG is treated as already transparent only when:
   *
   * 1. at least 1% of sampled pixels are strongly transparent (alpha <= 32)
   * AND
   * 2. either at least 8% of border pixels are strongly transparent,
   *    or at least 20% of all sampled pixels are strongly transparent,
   *    or at least two almost-clear full rows/columns form intentional gaps.
   *
   * If inspection fails for any reason, the engine safely falls back to
   * PhotoRoom rather than risking a white-background PNG being accepted.
   */
  const ALPHA_SAMPLE_MAX_SIDE = 256;
  const STRONG_ALPHA_MAX = 32;
  const MIN_OVERALL_TRANSPARENT_RATIO = 0.01;
  const MIN_BORDER_TRANSPARENT_RATIO = 0.08;
  const HIGH_OVERALL_TRANSPARENT_RATIO = 0.20;

  function getConfig() {
    return window.TissueMotifBackgroundConfig || {};
  }

  function isEnabled() {
    return getConfig().enabled === true;
  }

  function getEndpoint() {
    return String(getConfig().endpoint || '').trim();
  }

  function assertFile(file) {
    if (!(file instanceof File)) {
      throw new Error('A valid artwork file is required.');
    }

    if (!SUPPORTED_TYPES.has(file.type)) {
      throw new Error('Background removal supports PNG, JPG, and WebP files.');
    }

    if (file.size <= 0) {
      throw new Error('The artwork file is empty.');
    }

    if (file.size > MAX_FILE_BYTES) {
      throw new Error('The artwork file exceeds the 20 MB background-processing limit.');
    }
  }

  function transparentOutputName(file) {
    const raw = String(file.name || 'artwork');
    const base = raw.replace(/\.[^.]+$/, '') || 'artwork';
    return `${base}-transparent.png`;
  }

  async function loadImageForInspection(file) {
    if ('createImageBitmap' in window) {
      try {
        const bitmap = await createImageBitmap(file);
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          cleanup() {
            try {
              bitmap.close();
            } catch (_) {}
          }
        };
      } catch (_) {
        // Fall through to the object-URL image path.
      }
    }

    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        resolve({
          source: image,
          width: image.naturalWidth,
          height: image.naturalHeight,
          cleanup() {
            URL.revokeObjectURL(url);
          }
        });
      };

      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('The PNG could not be inspected.'));
      };

      image.src = url;
    });
  }

  async function inspectArtworkPixels(file) {
    if (!(file instanceof File) || !SUPPORTED_TYPES.has(file.type)) {
      return {
        inspected: false,
        meaningful: false,
        overallTransparentRatio: 0,
        borderTransparentRatio: 0
      };
    }

    let loaded = null;

    try {
      loaded = await loadImageForInspection(file);

      const naturalWidth = Number(loaded.width) || 0;
      const naturalHeight = Number(loaded.height) || 0;

      if (!naturalWidth || !naturalHeight) {
        return {
          inspected: false,
          meaningful: false,
          overallTransparentRatio: 0,
          borderTransparentRatio: 0
        };
      }

      const scale = Math.min(
        1,
        ALPHA_SAMPLE_MAX_SIDE / naturalWidth,
        ALPHA_SAMPLE_MAX_SIDE / naturalHeight
      );

      const width = Math.max(1, Math.round(naturalWidth * scale));
      const height = Math.max(1, Math.round(naturalHeight * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d', {
        alpha: true,
        willReadFrequently: true
      });

      if (!ctx) {
        return {
          inspected: false,
          meaningful: false,
          overallTransparentRatio: 0,
          borderTransparentRatio: 0
        };
      }

      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(loaded.source, 0, 0, width, height);

      const pixels = ctx.getImageData(0, 0, width, height).data;

      let transparentPixels = 0;
      let totalPixels = 0;
      let transparentBorderPixels = 0;
      let totalBorderPixels = 0;
      let alphaMass = 0, solidPixels = 0;
      const clearRows = new Uint32Array(height), clearColumns = new Uint32Array(width);

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const pixelIndex = (y * width + x) * 4;
          const alpha = pixels[pixelIndex + 3];
          const stronglyTransparent = alpha <= STRONG_ALPHA_MAX;
          alphaMass += alpha / 255;
          if (alpha >= 128) solidPixels += 1;

          totalPixels += 1;

          if (stronglyTransparent) {
            transparentPixels += 1;
            clearRows[y] += 1;
            clearColumns[x] += 1;
          }

          const isBorder =
            x === 0 ||
            y === 0 ||
            x === width - 1 ||
            y === height - 1;

          if (isBorder) {
            totalBorderPixels += 1;

            if (stronglyTransparent) {
              transparentBorderPixels += 1;
            }
          }
        }
      }

      const overallTransparentRatio =
        totalPixels > 0 ? transparentPixels / totalPixels : 0;

      const borderTransparentRatio =
        totalBorderPixels > 0
          ? transparentBorderPixels / totalBorderPixels
          : 0;

      // Narrow transparent gaps between several marks are genuine alpha too.
      // Require repeated, almost full-length channels, not isolated edge pixels.
      const transparentChannels =
        clearRows.filter(count => count >= width * .98).length >= 2 ||
        clearColumns.filter(count => count >= height * .98).length >= 2;
      const meaningful =
        overallTransparentRatio >= MIN_OVERALL_TRANSPARENT_RATIO &&
        (
          borderTransparentRatio >= MIN_BORDER_TRANSPARENT_RATIO ||
          overallTransparentRatio >= HIGH_OVERALL_TRANSPARENT_RATIO ||
          transparentChannels
        );

      return {
        inspected: true,
        meaningful,
        overallTransparentRatio,
        borderTransparentRatio,
        transparentChannels,
        alphaCoverage: totalPixels ? alphaMass / totalPixels : 0,
        solidCoverage: totalPixels ? solidPixels / totalPixels : 0
      };
    } catch (_) {
      /*
       * Inspection failure must never block a customer upload.
       * Treat the file as "not proven transparent" so it follows the
       * existing PhotoRoom path.
       */
      return {
        inspected: false,
        meaningful: false,
        overallTransparentRatio: 0,
        borderTransparentRatio: 0
      };
    } finally {
      if (loaded && typeof loaded.cleanup === 'function') {
        loaded.cleanup();
      }
    }
  }

  async function inspectPngTransparency(file) {
    return inspectArtworkPixels(file);
  }

  async function hasMeaningfulTransparency(file) {
    const result = await inspectPngTransparency(file);
    return result.meaningful === true;
  }

  async function removeBackground(file) {
    assertFile(file);

    const endpoint = getEndpoint();

    if (!endpoint) {
      throw new Error('Background removal endpoint is not configured.');
    }

    const formData = new FormData();
    formData.append('image_file', file, file.name || 'artwork');

    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin'
    });

    if (!response.ok) {
      let message = '';

      try {
        const payload = await response.json();
        message =
          payload?.error ||
          payload?.message ||
          '';
      } catch (_) {
        try {
          message = await response.text();
        } catch (_) {}
      }

      throw new Error(
        message ||
        `Background removal failed with HTTP ${response.status}.`
      );
    }

    const blob = await response.blob();

    if (!blob || blob.size <= 0) {
      throw new Error('Background removal returned an empty image.');
    }

    return new File(
      [blob],
      transparentOutputName(file),
      {
        type: 'image/png',
        lastModified: Date.now()
      }
    );
  }

  async function processArtwork(file, options = {}) {
    assertFile(file);

    if (!isEnabled()) {
      return {
        processed: false,
        file,
        reason: 'disabled'
      };
    }

    const originalPixels = await inspectArtworkPixels(file);
    // Both PNG and WebP can carry intentional transparency. Preserve it before
    // asking a segmentation service to guess which parts are the artwork.
    if (
      ['image/png', 'image/webp'].includes(file.type) &&
      options.forceBackgroundRemoval !== true
    ) {
      const transparency = originalPixels;

      if (transparency.meaningful) {
        return {
          processed: false,
          file,
          reason: 'already-transparent',
          transparency
        };
      }
    }

    const originalKept = (reason,error) => {
      if(options.preserveOriginalOnFailure!==true)throw error;
      return {processed: false, file, originalFile: file, reason,
      warning: 'Background could not be removed. Original artwork kept; use a transparent PNG for best results.'};
    };
    let processedFile;
    try {
      processedFile = await removeBackground(file);
    } catch (error) {
      return originalKept('processing-failed-original-kept',error);
    }
    const outputPixels = await inspectArtworkPixels(processedFile);
    // A nonempty PNG blob can still contain no usable artwork. Never replace
    // the upload with a nearly transparent segmentation result.
    if (!outputPixels.inspected || outputPixels.alphaCoverage < .003 ||
        outputPixels.solidCoverage < .002 ||
        originalPixels.inspected && outputPixels.alphaCoverage < originalPixels.alphaCoverage * .02) {
      return originalKept('unsafe-output-original-kept',new Error('Background removal returned no usable artwork. Please try a transparent PNG.'));
    }

    return {
      processed: true,
      file: processedFile,
      originalFile: file,
      reason: 'background-removed'
    };
  }

  window.TissueMotifBackgroundEngine = Object.freeze({
    version: VERSION,
    isEnabled,
    getEndpoint,
    processArtwork,
    removeBackground,
    inspectPngTransparency,
    hasMeaningfulTransparency
  });
})();
